import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇧🇷" },
];

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const { currentUser, businessId } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load language preference on mount and when user changes
  useEffect(() => {
    const loadLanguagePreference = async () => {
      try {
        // Priority: 1) User preference in Firestore, 2) localStorage, 3) browser language
        if (currentUser?.uid) {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists() && userDoc.data().language) {
            const userLang = userDoc.data().language;
            if (SUPPORTED_LANGUAGES.find(l => l.code === userLang)) {
              await i18n.changeLanguage(userLang);
              localStorage.setItem("effitrack_language", userLang);
              setLoading(false);
              return;
            }
          }
        }

        // Fallback to localStorage
        const savedLang = localStorage.getItem("effitrack_language");
        if (savedLang && SUPPORTED_LANGUAGES.find(l => l.code === savedLang)) {
          await i18n.changeLanguage(savedLang);
        }
      } catch (error) {
        console.error("Error loading language preference:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLanguagePreference();
  }, [currentUser, i18n]);

  const changeLanguage = useCallback(async (langCode) => {
    try {
      // Validate language code
      if (!SUPPORTED_LANGUAGES.find(l => l.code === langCode)) {
        throw new Error(`Unsupported language: ${langCode}`);
      }

      await i18n.changeLanguage(langCode);
      localStorage.setItem("effitrack_language", langCode);

      // Persist to Firestore if user is logged in
      if (currentUser?.uid) {
        try {
          await updateDoc(doc(db, "users", currentUser.uid), {
            language: langCode,
            updatedAt: new Date().toISOString()
          });
        } catch (firestoreError) {
          console.error("Error saving language to Firestore:", firestoreError);
          // Don't throw - language change was successful locally
        }
      }

      return true;
    } catch (error) {
      console.error("Error changing language:", error);
      throw error;
    }
  }, [currentUser, i18n]);

  const getCurrentLanguage = useCallback(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];
  }, [i18n.language]);

  const value = {
    currentLanguage: i18n.language,
    currentLanguageInfo: getCurrentLanguage(),
    changeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    loading,
    t: i18n.t,
    i18n,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageContext;
